/**
 * Shared canvas-group ordering + the single ungrouped sentinel.
 *
 * `UNGROUPED_KEY` is the Convex / filter / presence / preview encoding.
 * URL segments use `ungrouped`; the list table uses the display string
 * `'Ungrouped'`. Do not add a fourth sentinel — convert at the adapter.
 *
 * Manual order matches the /emails canvas: named groups sort by
 * `orderKey` DESC (larger key leftmost); ungrouped (no `orderKey`) sorts
 * by `createdAt` ASC (older leftmost). `sortDir` is ignored.
 */

export const UNGROUPED_KEY = '__ungrouped__'

/**
 * A name folded for comparison: trimmed, composed, lowercased, with the
 * Turkish dotted and dotless i read as one letter (`GRUPLANMAMIŞ`, typed in
 * capitals, still matches `Gruplanmamış`).
 */
function normalizeGroupName(name: string): string {
  return name
    .trim()
    .normalize('NFC')
    .toLowerCase()
    .replace(/\u0131/g, 'i')
    .replace(/\u0307/g, '')
}

/**
 * The built-in column's name in every catalog (`emails.groups.ungrouped`):
 * a group named like it in any reader's language would read as the column
 * itself. `tests/ungrouped-group-names.test.ts` keeps this list in step with
 * the catalogs.
 */
const UNGROUPED_COLUMN_NAMES: ReadonlySet<string> = new Set(
  [
    'Ungrouped',
    'Ohne Gruppe',
    'Sans groupe',
    'Sin grupo',
    'Sem grupo',
    'Senza gruppo',
    'Zonder groep',
    'Bez grupy',
    'Ogrupperade',
    'Nezařazené',
    'Gruplanmamış',
    'グループなし',
    '그룹 없음',
    'นอกกลุ่ม',
    'Chưa phân nhóm',
    'Tanpa grup',
  ].map(normalizeGroupName)
)

/**
 * Reserved folder names that must never become a real `groups` row: the
 * built-in column's name in any language, and the sentinel. Case-insensitive
 * so `Ungrouped` / `UNGROUPED` / `__ungrouped__` cannot mint a colliding
 * named group. Id sentinels stay exact — see `isPublicUngroupedGroupId` /
 * `isUngroupedGroupSentinel`.
 */
export function isReservedGroupName(name: string): boolean {
  const normalized = normalizeGroupName(name)
  return normalized === UNGROUPED_KEY || UNGROUPED_COLUMN_NAMES.has(normalized)
}

export type ManualOrderChat = {
  orderKey?: string | null
  createdAt?: string | null
}

/**
 * Within-row ordering rules (newest emails rightmost):
 *
 * 1. Among orderKey-bearing rows, sort orderKey DESCENDING. Convex assigns
 *    position=0 (newest insert) the smallest orderKey, so descending sort
 *    places the newest email at the rightmost slot — i.e. new emails are
 *    appended to the end of their row.
 *
 * 2. No-orderKey rows sort to the RIGHT of orderKey-bearing rows. As of the
 *    atomic-grouping change, `upsertStreamingEmail` writes the
 *    `emailGrouping` row in the same Convex transaction as the `emails` row
 *    insert, so a missing orderKey is a transient/historical state rather
 *    than the routine streaming case. Keeping these to the right matches
 *    rule (1)'s "newest rightmost" semantics for the brief window before
 *    the orderKey lands.
 *
 * 3. Among no-orderKey rows (ungrouped), older `createdAt` is leftmost.
 */
export function compareChatsByManualOrder(
  a: ManualOrderChat,
  b: ManualOrderChat
): number {
  const hasAKey = a.orderKey != null
  const hasBKey = b.orderKey != null

  if (hasAKey && hasBKey) {
    return (b.orderKey ?? '').localeCompare(a.orderKey ?? '')
  }
  // No-orderKey rows (just-streaming, pre-eager-grouping) go last (rightmost).
  if (!hasAKey && hasBKey) {
    return 1
  }
  if (hasAKey && !hasBKey) {
    return -1
  }

  const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
  const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
  return aTime - bTime
}

/** Preview / filter / presence key for a denormalized `emails.groupId`. */
export function previewGroupKey(groupId: string | undefined): string {
  return groupId ?? UNGROUPED_KEY
}
